/* eslint-disable react/no-multi-comp */
import React, {Component} from 'react';
import moment from 'moment';
import 'moment/locale/sv';
import classNames from 'classnames';
import strings from 'utils/strings';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPrint} from '@fortawesome/free-solid-svg-icons';

const NOT_AVAILABLE = strings.common.inteTillganglig;

const get = (object, path, fallback) => {
	if (!object || !path) {
		return fallback;
	}

	const value = path.split('.').reduce((current, key) => {
		if (current && Object.prototype.hasOwnProperty.call(current, key)) {
			return current[key];
		}

		return undefined;
	}, object);

	return value === undefined || value === null ? fallback : value;
};

const hasValue = (value) => {
	return value !== undefined && value !== null && value !== '';
};

const getContent = (props) => {
	if (hasValue(props.children)) {
		return props.children;
	}

	if (hasValue(props.value)) {
		return props.value;
	}

	return NOT_AVAILABLE;
};

const formatDate = (date) => {
	if (!date) {
		return NOT_AVAILABLE;
	}

	const parsedDate = moment(date);

	if (!parsedDate.isValid()) {
		return NOT_AVAILABLE;
	}

	return parsedDate.format('LL');
};

const formatAge = (age) => {
	const years = get(age, 'years');
	const months = get(age, 'months');

	if (!hasValue(years) && !hasValue(months)) {
		return NOT_AVAILABLE;
	}

	if (hasValue(years) && hasValue(months)) {
		return strings.formatString(strings.common.arOchManader, years, months);
	}

	if (hasValue(years)) {
		return strings.formatString(strings.common.arFormat, years);
	}

	return strings.formatString(strings.common.manaderFormat, months);
};

const formatGender = (gender, age) => {
	const years = get(age, 'years');
	const isChild = hasValue(years) && Number(years) < 18;

	if (gender === 'm') {
		return isChild ? strings.common.boy : strings.common.male;
	}

	if (gender === 'f') {
		return isChild ? strings.common.girl : strings.common.female;
	}

	if (gender === 'u') {
		return strings.common.ospecificerat;
	}

	return NOT_AVAILABLE;
};

const formatClassGrade = (classValue, education) => {
	const value = hasValue(classValue)
		? String(classValue)
		: hasValue(get(education, 'youthGrade'))
			? String(get(education, 'youthGrade'))
			: '';

	switch (value) {
		case '0':
			return strings.candidates.sistaAretForeSkolestart;
		case '1':
			return `${strings.common.arskurs} 1`;
		case '2':
			return `${strings.common.arskurs} 2`;
		case '3':
			return `${strings.common.arskurs} 3`;
		case '4':
			return `${strings.common.arskurs} 4`;
		case '5':
			return `${strings.common.arskurs} 5`;
		case '6':
			return `${strings.common.arskurs} 6`;
		case '7':
			return `${strings.common.arskurs} 7`;
		case '8':
			return `${strings.common.arskurs} 8`;
		case '9':
			return `${strings.common.arskurs} 9`;
		case '10':
			return `${strings.common.arskurs} 10`;
		case 'studiespesialisering':
			return strings.candidates.studieforberedandePro;
		case 'yrkesfag':
			return strings.candidates.yrkesprogram;
		case 'bachelor':
			return strings.candidates.kandidatexamen;
		case 'master':
			return strings.candidates.masterexamen;
		case 'phd':
			return strings.candidates.doktorsexamen;
		default:
			return NOT_AVAILABLE;
	}
};

const formatLanguagesInHome = (languagesInHome) => {
	switch (languagesInHome) {
		case 'one':
			return strings.candidates.ettSprak;
		case 'two':
			return strings.candidates.tvaSprak;
		case 'multiple':
			return strings.candidates.fleraSprak;
		default:
			return NOT_AVAILABLE;
	}
};

const InfoRow = (props) => {
	// eslint-disable-next-line no-undefined
	if (props.condition !== undefined && !props.condition) {
		return null;
	}

	return (
		<p className="info__row">
			<span className="info__label">{props.name}</span>
			<span className="info__value">{getContent(props)}</span>
		</p>
	);
};

class TestUserInfo extends Component {
	render() {
		const testResult = this.props.testResult || {};

		/*
			Normally the data is expected as:
			testResult.testUser

			This fallback also allows the component to survive if it receives
			a partial user object directly, for example:
			{ education: { youthGrade: 6 } }
		*/
		const testUser = testResult.testUser || testResult || {};

		const isTestOwner = !!testResult.isTestOwner;

		const firstName = get(testUser, 'firstName', '');
		const lastName = get(testUser, 'lastName', '');
		const birthdate = get(testUser, 'birthdate');
		const completedAt = get(testResult, 'completedAt');
		const age = get(testUser, 'age', {});
		const gender = get(testUser, 'gender');
		const education = get(testUser, 'education', {});

		const completeName = firstName
			? `${firstName} ${lastName || ''}`.trim()
			: '<dolt namn>';

		return (
			<div>
				<div className="lit-input-information">
					<button
						className={classNames('print-hide', {
							'lit-btn2 see-more': true,
							'bg-tertiary': true,
						})}
						onClick={() => {
							window.print();
						}}
					>
						<FontAwesomeIcon
							size="lg"
							className="lit-icon-btns"
							icon={faPrint}
						/>

						<p className="lit-btns-txt">
							{strings.testView.printInformation}
						</p>
					</button>
				</div>

				<div className="info">
					<InfoRow
						condition={isTestOwner}
						name={`${strings.common.name} `}
						value={completeName}
					/>

					<InfoRow
						condition={isTestOwner}
						name={strings.candidates.dateofbirth}
						value={formatDate(birthdate)}
					/>

					<InfoRow
						condition={hasValue(completedAt)}
						name={strings.common.testdate}
						value={formatDate(completedAt)}
					/>

					<InfoRow name={strings.common.age} value={formatAge(age)} />

					<InfoRow
						name={strings.common.sex}
						value={formatGender(gender, age)}
					/>

					<div className="info__group info__group--education">
						<h3>{strings.candidates.education}</h3>
					</div>

					<InfoRow
						condition={isTestOwner}
						name={`${strings.candidates.regionLabel} `}
						value={get(testUser, 'county', NOT_AVAILABLE)}
					/>

					<InfoRow
						condition={isTestOwner}
						name={`${strings.candidates.kommunLabel} `}
						value={get(testUser, 'municipality', NOT_AVAILABLE)}
					/>

					<InfoRow
						condition={isTestOwner}
						name={`${strings.candidates.skolaLabel} `}
						value={get(testUser, 'school', NOT_AVAILABLE)}
					/>

					<InfoRow
						name={`${strings.classes.classGrade}: `}
						value={formatClassGrade(
							get(testUser, 'class'),
							education
						)}
					/>

					<div className="info__group">
						<h3>{strings.candidates.homeLanugage}</h3>

						<InfoRow
							name={strings.candidates.vaxtUpp}
							value={formatLanguagesInHome(
								get(testUser, 'languagesInHome')
							)}
						/>
					</div>
				</div>
			</div>
		);
	}
}

export default TestUserInfo;
